import React, { useLayoutEffect, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rooms } from './data/rooms';
import Header from './components/layout/Header';
import Drawer from './components/layout/Drawer';
import SiteFooter from './components/layout/SiteFooter';
import useScroll from './utils/useScroll';

export default function RoomDetails() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const room = rooms.find((r) => r.id === parseInt(roomId));
  const heroImages = room ? room.images : [];
  const galleryImages = room ? room.images : [];
  const isScrolled = useScroll();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [heroCarouselIndex, setHeroCarouselIndex] = useState(0);
  const [galleryCarouselIndex, setGalleryCarouselIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchStartHero, setTouchStartHero] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    const touchEnd = e.changedTouches[0].clientX;
    if (touchStart - touchEnd > 75) {
      nextGalleryImage();
    }
    if (touchStart - touchEnd < -75) {
      prevGalleryImage();
    }
  };

  const handleTouchStartHero = (e) => {
    setTouchStartHero(e.touches[0].clientX);
  };

  const handleTouchEndHero = (e) => {
    const touchEndHero = e.changedTouches[0].clientX;
    if (touchStartHero - touchEndHero > 75) {
      nextHeroImage();
    }
    if (touchStartHero - touchEndHero < -75) {
      prevHeroImage();
    }
  };

  const nextHeroImage = useCallback(() => {
    setHeroCarouselIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
  }, [heroImages.length]);

  const prevHeroImage = useCallback(() => {
    setHeroCarouselIndex((prevIndex) => (prevIndex - 1 + heroImages.length) % heroImages.length);
  }, [heroImages.length]);

  const nextGalleryImage = useCallback(() => {
    setGalleryCarouselIndex((prevIndex) => (prevIndex + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const prevGalleryImage = useCallback(() => {
    setGalleryCarouselIndex((prevIndex) => (prevIndex - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  const openDrawer = () => {
    setDrawerOpen(true);
    document.documentElement.classList.add('menu-open');
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    document.documentElement.classList.remove('menu-open');
  };

  const handleMenuClick = () => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  };

  const handleBookNowClick = () => {
    navigate('/', { state: { notification: 'Check-in and check-out should not be empty' } });
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    window.history.scrollRestoration = 'manual'; // Disable browser's scroll restoration
  }, [roomId]);

  useEffect(() => {
    if (heroImages.length > 1) {
      const timer = setTimeout(() => {
        nextHeroImage();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [heroCarouselIndex, heroImages.length, nextHeroImage]);

  useEffect(() => {
    if (galleryImages.length > 1) {
      const timer = setTimeout(() => {
        nextGalleryImage();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [galleryCarouselIndex, galleryImages.length, nextGalleryImage]);

  if (!room) {
    return <div>Room not found</div>;
  }

  return (
    <>
      <Header scrolled={isScrolled} onMenuClick={handleMenuClick} />
      <Drawer isOpen={isDrawerOpen} onClose={closeDrawer} />

      <div className="font-['Cormorant_Garamond',serif] text-dark-green">
        {/* Hero Section */}
        <section
          className="relative bg-cover bg-center h-[70vh] flex items-center justify-center text-white cursor-pointer"
          style={{ backgroundImage: `url(${heroImages[heroCarouselIndex]})` }}
          onClick={nextHeroImage}
          onTouchStart={handleTouchStartHero}
          onTouchEnd={handleTouchEndHero}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <div className="relative z-10 text-center px-4" data-aos="fade-up">
            <h1 className="text-5xl sm:text-7xl font-bold leading-tight text-shadow-lg">{room.title}</h1>
            <p className="mt-4 text-xl sm:text-3xl italic text-shadow-md">{room.subtitle}</p>
          </div>
        </section>

        {/* Main Content Section */}
        <main className="bg-[#E1D9D1] py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column: Description and Amenities */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 rounded-lg shadow-xl border border-beige mb-12" data-aos="fade-up">
                <h2 className="text-4xl font-bold mb-6">About the {room.title}</h2>
                <p className="text-lg leading-relaxed text-gray-700">{room.description}</p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-xl border border-beige" data-aos="fade-up" data-aos-delay="100">
                <h3 className="text-3xl font-bold text-dark-green mb-4">Room Amenities</h3>
                <p className="text-gray-600 mb-6">All our rooms are comfortably furnished and thoughtfully equipped for a pleasant stay.</p>
                <div className="flex flex-wrap gap-3">
                  {room.amenities.map((amenity, index) => (
                    <div key={index} className="bg-white/50 border border-beige rounded-md px-4 py-2 text-base text-dark-green font-medium">
                      {amenity}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Gallery and Booking */}
            <div className="lg:col-span-1">
              {/* Image Gallery */}
              <div className="bg-white p-6 rounded-lg shadow-xl border border-beige mb-12" data-aos="fade-left">
                <h3 className="text-3xl font-bold mb-6">Gallery</h3>
                <div className="relative">
                  <div
                    className="overflow-hidden rounded-lg shadow-md border border-beige"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onClick={nextGalleryImage}
                  >
                    <img
                      src={galleryImages[galleryCarouselIndex]}
                      alt={`${room.title} ${galleryCarouselIndex + 1}`}
                      className="w-full h-64 object-cover gallery-image-hover"
                    />
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-2">Click on the image to see the next one.</p>
                </div>
              </div>

              <div className={`bg-white px-4 ${parseInt(roomId) === 2 ? 'py-[3.501039926937862rem]' : 'py-[2.792706879096rem]'} rounded-lg shadow-xl border border-beige flex flex-col justify-center`} data-aos="fade-left" data-aos-delay="100" style={{transform: 'translateY(-9%)'}}>
                <h3 className="text-2xl font-bold mb-4 text-dark-green text-center">Book Your Stay</h3>
                <p className="text-lg text-gray-600 mb-6 text-center">
                  Ready for an unforgettable stay? Check our availability and book your room today to secure the best rates.
                </p>
                <button
                  onClick={handleBookNowClick}
                  className="w-full rounded bg-dark-green px-4 py-2 text-base tracking-wider uppercase text-white hover:bg-opacity-90 transition-colors duration-300 shadow-lg"
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
      <SiteFooter />
    </>
  );
}